import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
/**
 * Users and groups (fuel.md section 5.10, as customers.md section 3.5). A user
 * is a local row linked to a Unified Access employee; identity fields live in
 * Unified Access and are updated there through the platform API.
 */
export const userStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
export const userRowSchema = z.object({
    id: z.string(),
    employeeId: z.string(),
    nameAr: z.string(),
    nameEn: z.string(),
    username: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    group: z.object({ id: z.string(), nameAr: z.string(), nameEn: z.string() }),
    branches: z.array(z.object({ id: z.string(), name: z.string() })),
    status: userStatusSchema,
    lastLoginAt: z.iso.datetime({ offset: true }).nullable(),
    createdAt: z.iso.datetime({ offset: true }),
});
export const userListQuerySchema = paginationQuerySchema.extend({
    status: userStatusSchema.optional(),
    groupId: z.string().optional(),
});
/**
 * One drawer (decision D8): either link an existing employee found by
 * username (`employeeId`) or create one in Unified Access with the names.
 */
export const createUserSchema = z
    .object({
    employeeId: z.string().min(1).optional(),
    nameAr: z.string().trim().min(1).max(120).optional(),
    nameEn: z.string().trim().min(1).max(120).optional(),
    username: z.string().trim().min(1).max(64),
    email: z.email().optional(),
    phone: z.string().trim().max(32).optional(),
    nfcId: z.string().trim().max(64).optional(),
    groupId: z.string().min(1),
    branchIds: z.array(z.string().min(1)).min(1),
})
    .refine((v) => v.employeeId !== undefined || v.nameAr !== undefined, {
    message: 'nameAr is required when creating a new employee',
    path: ['nameAr'],
});
export const createUserResultSchema = z.object({
    user: userRowSchema,
    /** Present when a new employee was created: whether UA emailed the setup link. */
    emailed: z.boolean().nullable(),
    /** One-time setup link to show once when no email was sent. */
    setupLink: z.string().nullable(),
});
export const updateUserSchema = z.object({
    nameAr: z.string().trim().min(1).max(120).optional(),
    nameEn: z.string().trim().min(1).max(120).optional(),
    email: z.email().optional(),
    phone: z.string().trim().max(32).optional(),
    groupId: z.string().min(1).optional(),
    branchIds: z.array(z.string().min(1)).min(1).optional(),
});
export const changeUserStatusSchema = z.object({ status: userStatusSchema });
export const resetPasswordResultSchema = z.object({
    emailed: z.boolean(),
    link: z.string().nullable(),
});
export const employeeLookupQuerySchema = z.object({ username: z.string().trim().min(1).max(64) });
export const employeeLookupResultSchema = z.object({
    employee: z
        .object({
        id: z.string(),
        username: z.string(),
        nameAr: z.string(),
        nameEn: z.string(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
    })
        .nullable(),
    /** True when this employee already has a Fuel user. */
    alreadyUser: z.boolean(),
});
export const groupStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
export const groupRowSchema = z.object({
    id: z.string(),
    nameAr: z.string(),
    nameEn: z.string(),
    descriptionAr: z.string().nullable(),
    descriptionEn: z.string().nullable(),
    status: groupStatusSchema,
    isSystem: z.boolean(),
    userCount: z.number().int(),
    serviceCount: z.number().int(),
});
export const groupDetailsSchema = groupRowSchema.extend({ serviceCodes: z.array(z.string()) });
export const groupListQuerySchema = paginationQuerySchema.extend({
    status: groupStatusSchema.optional(),
});
export const saveGroupSchema = z.object({
    nameAr: z.string().trim().min(1).max(120),
    nameEn: z.string().trim().min(1).max(120),
    descriptionAr: z.string().trim().max(500).optional(),
    descriptionEn: z.string().trim().max(500).optional(),
});
export const changeGroupStatusSchema = z.object({ status: groupStatusSchema });
/** The server stores the dependency closure of these codes (SH-09). */
export const setGroupServicesSchema = z.object({
    serviceCodes: z.array(z.string().min(1)).max(500),
});
/** GET /services: the POS services tree. */
export const serviceTreeSchema = z.array(z.object({
    code: z.string(),
    nameAr: z.string(),
    nameEn: z.string(),
    services: z.array(z.object({
        code: z.string(),
        nameAr: z.string(),
        nameEn: z.string(),
        entity: z.string(),
        app: z.enum(['DASHBOARD', 'OPERATOR', 'PDA', 'BOTH']),
    })),
}));
//# sourceMappingURL=users.contract.js.map